import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * Authorization Detail — sticky section anchors.
 *
 * ── WHY `sticky top-0` IS CORRECT HERE ──────────────────────────────────────
 * It looks wrong and is not. `AppLayout` is a fixed viewport shell: the root is
 * `h-screen overflow-hidden`, the global `<Header>` is a `shrink-0` flex sibling
 * with `h-16`, and the page scrolls inside `<main className="… overflow-auto">`.
 * The header therefore sits OUTSIDE the scrollport entirely — a `sticky top-0`
 * element inside the page pins to the top of `<main>`, which is already below
 * the header, and cannot overlap it. Adding a header-sized offset here would
 * leave a 4rem gap instead of preventing an overlap.
 *
 * This is the same reasoning `InspectionDetailPage`'s sticky header bar already
 * relies on, and this component copies its exact idiom:
 * `sticky top-0 z-10 -mx-6 px-6 bg-background/95 backdrop-blur-sm border-b`.
 * The negative margin cancels the `p-6` on AppLayout's content wrapper so the
 * bar spans the full width of the scroll area rather than floating inside the
 * page gutter.
 *
 * `z-10` matches that precedent, and stays below the header's own `z-50`
 * dropdown layer, so menus and dialogs continue to render above this bar.
 *
 * ── ANCHORING ───────────────────────────────────────────────────────────────
 * Real `<a href="#id">` inside a labelled `<nav>`, following the pattern the
 * Setup Guides already use (`GuideToc` in guide-primitives.tsx): keyboard
 * reachable for free, meaningful accessible names, and `aria-current` rather
 * than colour alone for the active state.
 *
 * The click is intercepted only to control scroll behaviour and to avoid
 * pushing a hash onto the router's location; `preventDefault()` then
 * `scrollIntoView` keeps the SPA route byte-identical and performs no
 * navigation at all. Keyboard activation (Enter) fires the same click handler,
 * so it takes the identical path.
 *
 * ── REDUCED MOTION ──────────────────────────────────────────────────────────
 * Satisfied by construction: this component introduces NO animation at all.
 * Section jumps are instant for everyone (see the note on the click handler for
 * why an animated variant was implemented, tested and then removed), and the
 * only transition here is `transition-colors` on the anchors' hover state — a
 * colour fade, which the project's `motion-reduce:` convention exists to gate
 * for movement, not for colour. No new reduced-motion system is introduced.
 *
 * ── OFFSET ──────────────────────────────────────────────────────────────────
 * Targets carry `scroll-mt-*` (see SECTION_SCROLL_MARGIN) so the heading lands
 * BELOW this bar rather than underneath it. That is the existing project
 * pattern — the Setup Guide sections use `scroll-mt-24` for the same purpose.
 */

export interface AuthorizationSection {
  id: string;
  label: string;
}

/**
 * Applied to every anchor target. Must clear this bar's height (py-2 + a ~34px
 * control row ≈ 3rem) with a little breathing room; `scroll-mt-20` (5rem) does
 * that and matches the scale of the guides' `scroll-mt-24`.
 */
export const SECTION_SCROLL_MARGIN = 'scroll-mt-20';

/**
 * Nearest scrollable ancestor — the element that actually scrolls. In this app
 * that is AppLayout's `<main className="… overflow-auto">`, never the document.
 * Returns null when none is found, in which case the viewport is the scroll port.
 */
function scrollParentOf(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

/**
 * Active-section tracking, mirroring `useTocScrollSpy` in the Setup Guides
 * (same feature detection, same "topmost visible section wins" rule).
 *
 * Deliberately re-stated here rather than imported: that helper lives in the
 * Platform Console branch (`features/platform/storage`), and the tenant app and
 * platform app are kept as separate branches per D-050. Importing across that
 * boundary to save nine lines would couple them for no benefit.
 *
 * Feature-detected because `IntersectionObserver` does not exist in jsdom, and
 * the anchors must work without it — the bar is fully functional with no active
 * highlight at all.
 */
function useActiveSection(ids: readonly string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  const key = ids.join('|');

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const elements = key
      .split('|')
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    // ── THE ROOT MUST BE THE SCROLL CONTAINER, NOT THE VIEWPORT ─────────────
    // This page scrolls inside AppLayout's `<main overflow-auto>`, not the
    // document. Observing against the default viewport root was measured to
    // stick on whichever section happened to report last and never update
    // again while scrolling — the highlight was simply wrong. Rooting the
    // observer at the real scroll port fixes it and makes the percentage in
    // `rootMargin` resolve against the container the user is actually
    // scrolling. Falls back to the viewport when no scroll parent exists.
    const root = scrollParentOf(elements[0]);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActive(visible.target.id);
      },
      // Top margin clears the sticky bar so the section under it is not counted
      // as current; the -70% bottom keeps only the upper band in play, so one
      // section wins rather than every section on screen.
      { root, rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    );
    for (const el of elements) observer.observe(el);
    return () => observer.disconnect();
    // Depends on the joined `key` ALONE, never on the array. `sections` is
    // rebuilt on every render of the page, so an array dependency would re-run
    // this effect on every render, disconnecting and recreating the observer
    // each time — churn that serves no purpose and risks losing the first
    // callback. The string is stable whenever the section list is unchanged.
  }, [key]);

  return active;
}

interface Props {
  /** Only sections that are actually rendered on the page. */
  sections: readonly AuthorizationSection[];
}

export function AuthorizationSectionNav({ sections }: Props) {
  const ids = sections.map((s) => s.id);
  const active = useActiveSection(ids);

  if (sections.length === 0) return null;

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    const target = document.getElementById(id);
    if (!target) return; // Fall through to the browser's own anchor handling.
    event.preventDefault();

    // ── WHY THIS JUMPS INSTEAD OF ANIMATING ─────────────────────────────────
    // `behavior: 'auto'`, for every user, to match the project: there is no
    // smooth scrolling anywhere in this codebase (`scroll-smooth` appears zero
    // times), and the direct precedent — the Setup Guides' table of contents —
    // uses plain anchors that jump. A single animated surface would be the odd
    // one out, and an instant jump behaves identically in every environment.
    //
    // This also makes reduced motion correct by construction: no animation is
    // produced, so there is none for a reduced-motion user to be spared. That
    // is why no `prefers-reduced-motion` branch is needed here.
    target.scrollIntoView({ behavior: 'auto', block: 'start' });
  };

  return (
    <nav
      aria-label="Authorization sections"
      data-testid="authorization-section-nav"
      className="sticky top-0 z-10 -mx-6 border-b border-border bg-background/95 px-6 py-2 backdrop-blur-sm"
    >
      {/* Horizontal scroll lives on the bar itself, so a long section list can
          never push the page into horizontal overflow. */}
      <ul className="flex items-center gap-1 overflow-x-auto">
        {sections.map((s) => (
          <li key={s.id} className="shrink-0">
            <a
              href={`#${s.id}`}
              onClick={(e) => handleClick(e, s.id)}
              aria-current={active === s.id ? 'true' : undefined}
              className={cn(
                'block rounded-md px-2.5 py-1 text-xs transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                active === s.id
                  ? // Weight and an underline carry the active state as well as
                    // colour, so it survives a colour-blind or high-contrast view.
                    'font-semibold text-primary underline decoration-2 underline-offset-4'
                  : 'font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground',
              )}
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}
